import { supabase, isSupabaseReady } from '../services/supabase.js';
import { renderNavbar } from '../components/navbar.js';
import { renderDebriefCard } from '../components/debrief-card.js';
import { calculateBasePoints, levelFromPoints } from '../utils/gamification.js';
import { renderAvatarInner } from '../utils/avatar.js';
import { escapeHtml } from '../utils/html.js';
import { summarizeReactionsByDebrief } from '../utils/reactions.js';
import { t, tr } from '../services/i18n.js';

async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

function emptyState(message, action = '') {
  return `
    <div class="empty-state">
      <div style="font-size:32px; margin-bottom:12px; opacity:0.5;">⚽</div>
      <p class="heading-3" style="margin-bottom:8px; font-size:16px;">${t('homeEmptyTitle')}</p>
      <p class="text-muted" style="font-size:13px; line-height:1.6;">${message}</p>
      ${action}
    </div>
  `;
}

export async function renderHome(container) {
  container.innerHTML = `
    <div class="page app-shell">

      <!-- Header -->
      <header class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">Fooview</p>
            <p class="brand-subtitle">${t('brandSubtitle')}</p>
          </div>
        </div>
        <a class="btn-primary" href="#/upload" id="btn-new-debrief" style="font-family:var(--font-ui); letter-spacing:-0.01em;">
          <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          ${tr('Nouvelle analyse', 'New analysis')}
        </a>
      </header>

      <!-- Cockpit Profil -->
      <section id="cockpit-stats-panel" class="panel">
        <div class="skeleton" style="height:80px; border-radius:10px;"></div>
      </section>

      <!-- Raccourcis -->
      <section class="grid-3">
        <a class="stat-card" href="#/competitions" style="text-decoration:none; display:block; border-top:2px solid var(--primary);">
          <span>${tr('Compétitions', 'Competitions')}</span>
          <strong>${tr('CDM · Ligues · Clubs', 'World Cup · Leagues · Clubs')}</strong>
        </a>
        <a class="stat-card" href="#/quiz" style="text-decoration:none; display:block; border-top:2px solid var(--gold);">
          <span>${tr('Défis Tactiques', 'Tactical Challenges')}</span>
          <strong>${tr('Quizzes & Analyses', 'Quizzes & Analyses')}</strong>
        </a>
        <a class="stat-card" href="#/profile" style="text-decoration:none; display:block; border-top:2px solid rgba(255,255,255,0.18);">
          <span>${tr('Progression', 'Progress')}</span>
          <strong>${tr('Objectifs & Niveaux', 'Goals & Levels')}</strong>
        </a>
      </section>

      <!-- Feed + Sidebar -->
      <section class="feed-layout">
        <div class="panel" style="display:flex; flex-direction:column; gap:0;">
          <div class="section-head">
            <div>
              <p class="eyebrow">${tr('Flux Tactique', 'Tactical Feed')}</p>
              <h2 class="heading-2">${tr('Dernières Analyses', 'Latest Analyses')}</h2>
            </div>
          </div>
          <div id="feed-skeleton" class="feed-list">
            <div class="skeleton" style="height:140px;"></div>
            <div class="skeleton" style="height:140px;"></div>
          </div>
          <div id="feed-list" class="feed-list"></div>
        </div>

        <aside style="display:flex; flex-direction:column; gap:16px; height:fit-content;">
          <div class="panel">
            <p class="eyebrow">${tr('Comment commencer', 'How to Start')}</p>
            <div style="display:flex; flex-direction:column; gap:10px; margin-top:12px;">
              <div style="padding:12px 14px; background:var(--surface-2); border-radius:var(--radius-md); border-left:3px solid var(--primary);">
                <span style="font-size:10px; text-transform:uppercase; font-weight:700; letter-spacing:0.08em; color:var(--muted); display:block; margin-bottom:4px;">${tr('Étape 1', 'Step 1')}</span>
                <p style="margin:0; font-size:13px; font-weight:600; color:var(--ink);">${tr('Sélectionnez un match et uploadez une séquence courte (15–30s).', 'Select a match and upload a short clip (15-30s).')}</p>
              </div>
              <div style="padding:12px 14px; background:var(--surface-2); border-radius:var(--radius-md); border-left:3px solid var(--gold);">
                <span style="font-size:10px; text-transform:uppercase; font-weight:700; letter-spacing:0.08em; color:var(--muted); display:block; margin-bottom:4px;">${tr('Étape 2', 'Step 2')}</span>
                <p style="margin:0; font-size:13px; font-weight:600; color:var(--ink);">${tr('Posez votre question et découvrez le schéma de terrain IA.', 'Ask your question and review the AI tactical board.')}</p>
              </div>
              <div style="padding:12px 14px; background:var(--surface-2); border-radius:var(--radius-md); border-left:3px solid rgba(255,255,255,0.15);">
                <span style="font-size:10px; text-transform:uppercase; font-weight:700; letter-spacing:0.08em; color:var(--muted); display:block; margin-bottom:4px;">${tr('Étape 3', 'Step 3')}</span>
                <p style="margin:0; font-size:13px; font-weight:600; color:var(--ink);">${tr("Défiez vos amis avec un quiz tactique masquant l'analyse.", 'Challenge friends with a tactical quiz that hides the AI answer.')}</p>
              </div>
            </div>
          </div>
          <div class="panel" style="text-align:center;">
            <p style="font-size:11px; text-transform:uppercase; font-weight:700; letter-spacing:0.08em; color:var(--muted); margin-bottom:10px;">${tr('Votre première analyse', 'Your first analysis')}</p>
            <a href="#/upload" class="btn-primary" style="display:block; width:100%;">
              ${tr('Commencer maintenant', 'Start now')}
            </a>
          </div>
        </aside>
      </section>

    </div>
  `;

  renderNavbar(container);

  const list     = container.querySelector('#feed-list');
  const skeleton = container.querySelector('#feed-skeleton');
  const cockpit  = container.querySelector('#cockpit-stats-panel');

  if (!isSupabaseReady()) {
    skeleton.style.display = 'none';
    cockpit.style.display  = 'none';
    list.innerHTML = emptyState(tr("Supabase n'est pas configuré. Ajoutez VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY.", 'Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'));
    return;
  }

  try {
    let user = null, profile = null;
    let debriefCount = 0, commentCount = 0, reactionCount = 0;

    user = await getCurrentUser();
    if (user) {
      const [pRes, dR, cR, rR] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('debriefs').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('comments').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('reactions').select('debrief:debriefs!inner(user_id)', { count: 'exact', head: true }).eq('debrief.user_id', user.id)
      ]);
      profile       = pRes.data;
      debriefCount  = dR.count || 0;
      commentCount  = cR.count || 0;
      reactionCount = rR.count || 0;
    }

    const stats  = { debriefs: debriefCount, comments: commentCount, reactionsReceived: reactionCount, quizEntries: 0 };
    const points = calculateBasePoints(stats);
    const lvl    = levelFromPoints(points);
    const pct    = Math.min(100, Math.round((lvl.current / lvl.next) * 100));

    if (user && profile) {
      cockpit.innerHTML = `
        <div style="display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap;">
          <div style="display:flex; align-items:center; gap:14px;">
            <div style="width:52px; height:52px; border-radius:50%; background:linear-gradient(135deg,var(--primary),#0a3d22); display:grid; place-items:center; font-size:22px; color:#fff; border:1px solid var(--glass-border); overflow:hidden; flex-shrink:0;">
              ${renderAvatarInner(profile)}
            </div>
            <div>
              <p style="margin:0; font-family:var(--font-ui); font-weight:800; font-size:17px; color:var(--ink);">@${escapeHtml(profile.pseudo || tr('analyste', 'analyst'))}</p>
              <p style="margin:2px 0 0; font-size:11px; color:var(--primary-h); font-weight:700; text-transform:uppercase; letter-spacing:0.06em;">${escapeHtml(lvl.label)}</p>
            </div>
          </div>
          <div style="flex:1; min-width:200px; max-width:380px;">
            <div style="display:flex; justify-content:space-between; font-size:12px; font-weight:700; margin-bottom:8px; font-family:var(--font-ui);">
              <span style="color:var(--ink-2);">${tr('Niveau', 'Level')} ${lvl.level}</span>
              <span style="color:var(--gold-light);">${points} pts</span>
            </div>
            <div class="progress-bar"><div class="progress-fill" style="width:${pct}%;"></div></div>
          </div>
        </div>
      `;
    } else {
      cockpit.style.display = 'none';
    }

    let rows = [];
    const { data, error } = await supabase
      .from('debriefs')
      .select(`id, video_key, title, summary, key_moment, visibility, created_at,
        competition:competitions(id,name,code),
        match:matches(id,home_team,away_team,kickoff_at),
        user:profiles!debriefs_user_id_fkey(id,pseudo,avatar_url,avatar_emoji)`)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error && /competition|match|relationship|column/i.test(error.message || '')) {
      const retry = await supabase.from('debriefs')
        .select(`id,video_key,title,summary,key_moment,created_at,user:profiles!debriefs_user_id_fkey(id,pseudo,avatar_url,avatar_emoji)`)
        .order('created_at',{ascending:false}).limit(20);
      if (retry.error) throw retry.error;
      rows = retry.data;
    } else if (error) {
      throw error;
    } else {
      rows = data;
    }

    skeleton.style.display = 'none';

    if (!rows || rows.length === 0) {
      list.innerHTML = emptyState(
        tr('Le feed est vide. Lancez votre première analyse vidéo pour alimenter le flux.', 'The feed is empty. Run your first video analysis to fill it.'),
        `<div style="margin-top:16px;"><a class="btn-primary" href="#/upload" style="display:inline-flex;">${tr('Lancer ma première analyse', 'Run my first analysis')}</a></div>`
      );
      return;
    }

    const debriefIds = rows.map((row) => row.id).filter(Boolean);
    if (debriefIds.length) {
      const { data: reactions, error: reactionsError } = await supabase
        .from('reactions')
        .select('debrief_id,emoji')
        .in('debrief_id', debriefIds);

      if (reactionsError) throw reactionsError;

      const reactionSummaries = summarizeReactionsByDebrief(reactions || []);
      rows = rows.map((row) => ({
        ...row,
        reaction_counts: reactionSummaries.get(row.id) || { fire: 0, brain: 0, cap: 0 }
      }));
    }

    for (const debrief of rows) {
      list.appendChild(renderDebriefCard(debrief));
    }

  } catch (e) {
    skeleton.style.display = 'none';
    cockpit.style.display  = 'none';
    list.innerHTML = emptyState(tr('Erreur de chargement : ', 'Loading error: ') + e.message);
  }
}
